<template>
  <div class="cal" :class="{ 'cal--fill': isTimeGrid }">
    <!-- 컨트롤 한 줄: 날짜 이동 + 범위 + 종류 + 뷰 -->
    <div ref="barRef" class="cal__bar">
      <button type="button" class="cal__today" @click="goToday">오늘</button>

      <div class="cal__nav">
        <button type="button" class="cal__navbtn" aria-label="이전" @click="step(-1)">
          <el-icon><ArrowLeft /></el-icon>
        </button>
        <button type="button" class="cal__navbtn" aria-label="다음" @click="step(1)">
          <el-icon><ArrowRight /></el-icon>
        </button>
      </div>

      <h2 class="cal__period u-num">{{ periodLabel }}</h2>

      <div class="cal__right">
        <ScopeControl
          :scope="q.scope"
          :user-id="q.userId"
          :dept-id="q.deptId"
          :users="orgUsers"
          :departments="departments"
          @change="onScopeChange"
        />
        <button
          type="button"
          class="cal__filter"
          :class="{ 'is-on': kindsFiltered }"
          aria-label="표시할 항목"
          @click="filterOpen = true"
        >
          <el-icon><Filter /></el-icon>
        </button>
        <ViewSwitcher class="cal__switch" :model-value="q.view" :views="availableViews" @update:model-value="setView" />
      </div>
    </div>

    <!-- 본문 -->
    <div ref="bodyRef" class="cal__body">
      <UnifiedMonthView
        v-if="q.view === 'month'"
        :items="items"
        :date="anchorDate"
        @day-click="openDay"
        @item-click="openItem"
        @item-move="onItemMove"
        @overflow-click="openDay"
      />

      <AgendaListView
        v-else-if="q.view === 'agenda'"
        :items="items"
        :year="anchorDate.getFullYear()"
        @open-item="openItem"
      />

      <!--
        @create / @update 를 반드시 받아야 한다.
        CalendarTimeGrid 는 둘 다 emit 하는데 여기서 안 받으면 주/일 뷰에서
        드래그로 시간을 그어도 아무 일이 없고, 일정 블록을 옮겨도 조용히
        제자리로 돌아온다 — 되는 것처럼 보이는데 안 되는 상태였다.
      -->
      <CalendarTimeGrid
        v-else
        :items="items"
        :view-type="q.view"
        :current-date="anchorDate"
        @item-click="openItem"
        @create="onGridCreate"
        @update="onGridUpdate"
      />
    </div>

    <!-- 기한 없는 업무 -->
    <BacklogRail v-if="backlog.length" :items="backlog" @item-click="openItem" />

    <!-- FAB 이 openCompose('schedule') 로 못박혀 있어서 캘린더에서 가장 눈에 띄는
         + 버튼으로 업무를 만들 수 없었다. openCompose 는 이미 kind 로 분기하고
         (:371) 일자 시트·상단바 경로도 그렇게 부르는데 FAB 만 한쪽으로 굳어 있었다.
         탭이 1회 늘지만 지금 불가능한 동작이 가능해진다. -->
    <Fab label="추가" @click="composeSheetOpen = true" />

    <ResponsiveDialog v-model="composeSheetOpen" title="무엇을 추가할까요" width="380px">
      <div class="cal__pick">
        <button type="button" class="cal__pick-btn" @click="pickCompose('schedule')">
          <span class="cal__pick-t">일정</span>
          <span class="cal__pick-d">근무 · 대직 · 외근 · 휴무 · 휴가</span>
        </button>
        <button type="button" class="cal__pick-btn" @click="pickCompose('task')">
          <span class="cal__pick-t">업무</span>
          <span class="cal__pick-d">해야 할 일</span>
        </button>
      </div>
    </ResponsiveDialog>

    <!-- 일자 시트 -->
    <AgendaDaySheet
      v-model="daySheetOpen"
      :date="sheetDate"
      :items="dayItems"
      :loading="loading"
      @prev-day="shiftDay(-1)"
      @next-day="shiftDay(1)"
      @open-item="openItem"
      @toggle-done="toggleDone"
      @compose="onComposeFromSheet"
    />

    <!-- 종류 필터 -->
    <ResponsiveDialog v-model="filterOpen" title="표시할 항목" width="380px">
      <el-checkbox-group v-model="kindsDraft" class="cal__kinds">
        <el-checkbox label="schedule">일정</el-checkbox>
        <el-checkbox label="task">업무</el-checkbox>
      </el-checkbox-group>
      <template #footer>
        <el-button @click="filterOpen = false">취소</el-button>
        <el-button type="primary" @click="applyKinds">적용</el-button>
      </template>
    </ResponsiveDialog>

    <!-- 작성 / 상세 -->
    <!-- EventDialog 는 v-model:visible 과 event-saved 를 쓴다(구 계약 유지) -->
    <EventDialog
      v-model:visible="eventDialogOpen"
      :event="editingEvent"
      :default-date="composeDateObj"
      :default-end-date="composeEndObj"
      :default-all-day="composeAllDay"
      @event-saved="reload"
      @event-deleted="reload"
    />
    <TaskDialog
      v-model="taskDialogOpen"
      :task="editingTask"
      :preset-date="composeDate"
      @saved="reload"
    />
    <EventInfoDialog
      v-model="eventInfoOpen"
      :event="viewingEvent"
      @refresh="reload"
    />
  </div>
</template>

<script>
import { ref, computed, watch, onMounted, onActivated, onDeactivated, onBeforeUnmount } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import { ArrowLeft, ArrowRight, Filter } from '@element-plus/icons-vue'

import { useAgendaQuery } from '@/composables/useAgendaQuery'
import { useBreakpoint } from '@/composables/useBreakpoint'
import { useSwipeNavigation } from '@/composables/useSwipeNavigation'
import { monthGridRange } from '@/composables/useMonthGrid'
import {
  addDaysYmd, diffDaysYmd, itemsOnDate, shiftAgendaItemDays, shiftServerDateTime, toServerDateTime, ymd
} from '@/utils/agendaItem'

import UnifiedMonthView from '@/components/calendar/UnifiedMonthView.vue'
import CalendarTimeGrid from '@/components/calendar/CalendarTimeGrid.vue'
import AgendaListView from '@/components/agenda/AgendaListView.vue'
import AgendaDaySheet from '@/components/agenda/AgendaDaySheet.vue'
import BacklogRail from '@/components/agenda/BacklogRail.vue'
import ScopeControl from '@/components/common/ScopeControl.vue'
import ViewSwitcher from '@/components/common/ViewSwitcher.vue'
import Fab from '@/components/common/Fab.vue'
import ResponsiveDialog from '@/components/dialogs/ResponsiveDialog.vue'
import EventDialog from '@/components/EventDialog.vue'
import EventInfoDialog from '@/components/EventInfoDialog.vue'
import TaskDialog from '@/components/tasks/TaskDialog.vue'

/**
 * 통합 캘린더 — 일정과 업무를 한 화면에서 본다.
 *
 * 이전 구현(1,000줄)과의 핵심 차이:
 *
 *  1. 상태를 URL 쿼리 하나로 둔다.
 *     예전에는 path param / ?id / ?mine / ?date / ?agenda / myEventsOnly /
 *     selectedAgendaDate 각각에 양방향 watcher 를 달아 7개를 돌렸고, 그중 여럿이
 *     loadEvents() 를 불러서 중복 fetch 가드까지 붙어 있었다.
 *     지금은 watch 하나가 로딩을 트리거하고, 모든 사용자 액션은 URL 만 바꾼다.
 *     루프가 생길 여지가 없고, 새로고침/공유 링크가 화면을 그대로 복원한다.
 *
 *  2. 일정과 업무를 한 API(/api/agenda)에서 같은 형태로 받는다.
 *     FullCalendar 잔재였던 { start,end } + { startDate,endDate } +
 *     { extendedProps } 삼중 형태가 사라졌다.
 */
export default {
  // AppShell 의 keep-alive include 와 일치해야 한다
  name: 'CalendarPage',
  components: {
    UnifiedMonthView, CalendarTimeGrid, AgendaListView, AgendaDaySheet, BacklogRail,
    ScopeControl, ViewSwitcher, Fab, ResponsiveDialog,
    EventDialog, EventInfoDialog, TaskDialog,
    ArrowLeft, ArrowRight, Filter
  },
  setup() {
    const store = useStore()
    const route = useRoute()
    const router = useRouter()
    const { isMobile } = useBreakpoint()
    const { state: q, patch, openSheet, closeSheet } = useAgendaQuery({ view: 'month', scope: 'all' })

    // 이 화면이 지금 라우트인가. keep-alive 때문에 필요하다 — 아래 쿼리 watcher 주석 참고.
    const onPage = () => route.name === 'Calendar'

    const bodyRef = ref(null)
    const barRef = ref(null)
    const items = ref([])
    const backlog = ref([])
    const loading = ref(false)

    const filterOpen = ref(false)
    const composeSheetOpen = ref(false)
    const kindsDraft = ref([])

    const eventDialogOpen = ref(false)
    const taskDialogOpen = ref(false)
    const eventInfoOpen = ref(false)
    const editingEvent = ref(null)
    const editingTask = ref(null)
    const viewingEvent = ref(null)
    const composeDate = ref(null)
    /*
     * 타임그리드에서 드래그로 그은 시간 범위. 날짜만 있는 FAB·일자 시트 경로와
     * 구분하려고 따로 둔다 — 그쪽은 종일이 기본이고, 여기는 사용자가 그은
     * 범위 자체가 의도다.
     */
    const composeStartAt = ref(null)
    const composeEndAt = ref(null)
    const composeAllDay = ref(true)

    const anchorDate = computed(() => new Date(`${q.value.date}T00:00:00`))
    // EventDialog 는 Date 객체를 받는다.
    // 드래그로 시각까지 정해진 경우에는 그 Date 를 그대로 넘긴다.
    const composeDateObj = computed(() => {
      if (composeStartAt.value) return composeStartAt.value
      return composeDate.value ? new Date(`${composeDate.value}T09:00:00`) : new Date()
    })
    const composeEndObj = computed(() => composeEndAt.value)
    const orgUsers = computed(() => store.state.orgUsers)
    const departments = computed(() => store.state.orgDepartments)

    // 모바일에서 주 뷰는 실사용이 어렵다(30분 슬롯 × 7컬럼). 월/일/목록만 노출한다.
    const availableViews = computed(() =>
      isMobile.value ? ['month', 'day', 'agenda'] : ['month', 'week', 'day', 'agenda']
    )

    const kindsFiltered = computed(() => q.value.kinds.length !== 2)

    const daySheetOpen = computed({
      // onPage: 다른 화면의 ?sheet=day 로 이 시트가 뒤에서 열리지 않게(아래 onPage 주석)
      get: () => onPage() && q.value.sheet === 'day',
      set: (v) => { if (!v) closeSheet() }
    })

    /*
     * 일자 시트의 날짜. sheetId 가 날짜가 아니면 보고 있는 날짜로 떨어진다.
     * 예전에는 그대로 썼는데, 보고서의 일정 항목 링크가 sheetId 에 일정 id 를 실어 보내서
     * ('?sheet=day&sheetId=3138') 시트 제목이 'NaN월 NaN일' 로 열리고 목록이 비었다.
     */
    const sheetDate = computed(() =>
      /^\d{4}-\d{2}-\d{2}$/.test(q.value.sheetId) ? q.value.sheetId : q.value.date
    )
    const dayItems = computed(() => itemsOnDate(items.value, sheetDate.value))

    /*
     * 주/일 뷰는 화면 높이에 맞춘 상자 안에서 스크롤한다.
     * 예전에는 .cal 에 min-height 만 있어서 타임그리드가 하루치(1,440px)로 다 펴졌고,
     * 실제 스크롤은 창이 했다. 그러면 그리드 안의 sticky 날짜 헤더는 붙을 데가 없어
     * 오후 시간대를 보면 어느 칸이 무슨 요일인지 사라졌고, 처음 열 때 08시로 내려 두는
     * 코드(CustomTimeGrid onMounted)도 스크롤되지 않는 상자에 scrollTop 을 써서
     * 매번 00시부터 보였다.
     */
    const isTimeGrid = computed(() => q.value.view === 'week' || q.value.view === 'day')

    const periodLabel = computed(() => {
      const d = anchorDate.value
      if (q.value.view === 'day') return `${d.getFullYear()}년 ${d.getMonth() + 1}월 ${d.getDate()}일`
      return `${d.getFullYear()}년 ${d.getMonth() + 1}월`
    })

    /** 현재 뷰가 필요로 하는 조회 범위 */
    function currentRange() {
      const d = anchorDate.value
      if (q.value.view === 'month' || q.value.view === 'agenda') return monthGridRange(d)
      if (q.value.view === 'week') {
        const s = new Date(d); s.setDate(d.getDate() - d.getDay())
        const e = new Date(s); e.setDate(s.getDate() + 6)
        return { from: ymd(s), to: ymd(e) }
      }
      // 일 뷰는 앞뒤로 여유를 둬 스와이프 이동 때마다 재조회하지 않게 한다
      const s = new Date(d); s.setDate(d.getDate() - 3)
      const e = new Date(d); e.setDate(d.getDate() + 3)
      return { from: ymd(s), to: ymd(e) }
    }

    /*
     * 응답 순서 가드.
     * 월을 빠르게 넘기거나 이동 저장 직후의 reload 가 겹치면 늦게 보낸 요청이
     * 먼저 도착할 수 있다. 마지막으로 보낸 요청의 결과만 화면에 반영한다.
     */
    let loadSeq = 0

    /*
     * 저장이 진행 중인 드래그 이동. uid → 옮겨진 항목.
     *
     * 서버 스냅샷은 아직 이 저장을 모를 수 있다. 두 건을 연달아 옮기면
     * 첫 번째의 reload 응답(두 번째 저장 이전에 나간 요청)이 두 번째 칩을
     * 옛 날짜로 되돌려 놓았다가, 두 번째 reload 가 다시 옮기는 깜빡임이 난다.
     * load() 가 끝난 뒤 진행 중인 이동만 다시 씌워서 그 왕복을 없앤다.
     */
    const pendingMoves = new Map()

    async function load() {
      const { from, to } = currentRange()
      const seq = ++loadSeq
      loading.value = true
      try {
        const res = await store.dispatch('fetchAgenda', {
          from,
          to,
          scope: q.value.scope,
          userId: q.value.userId,
          departmentId: q.value.deptId,
          kinds: q.value.kinds,
          q: q.value.q,
          includeUnscheduled: true
        })
        if (seq !== loadSeq) return // 더 최신 요청이 이미 나갔다
        items.value = pendingMoves.size
          ? res.items.map((it) => pendingMoves.get(it.uid) || it)
          : res.items
        backlog.value = res.unscheduled
      } catch (e) {
        if (seq !== loadSeq) return
        items.value = []
        backlog.value = []
        ElMessage.error(e.response?.data?.message || '일정을 불러오는데 실패했습니다.')
      } finally {
        if (seq === loadSeq) loading.value = false
      }
    }

    async function reload() {
      await store.dispatch('invalidateAgenda')
      await load()
    }

    /*
     * 이 화면은 keep-alive 로 살아 있어서 **다른 화면에 있는 동안에도** watcher 가 돈다.
     * q 는 지금 라우트의 쿼리를 읽으므로, 업무 화면의 ?status= 나 상단바의
     * ?compose=task 까지 캘린더 쿼리로 읽힌다. 그대로 두면
     *   · 다른 화면에서 쿼리가 바뀔 때마다 캘린더가 오늘 달을 몰래 다시 조회하고
     *   · '새로 만들기 › 업무' 가 /tasks?compose=task 로 가는 순간 캘린더가 먼저
     *     그 쿼리를 집어 자기 업무 작성창을 열고 쿼리를 지워 버린다
     * 캘린더 라우트에 있고 활성 상태일 때만 반응한다. 떠나 있던 동안의 변화는 onActivated 가
     * 한 번에 맞춘다(돌아오는 순간에는 라우트 watcher 가 활성화보다 먼저 돌아서, 여기서도
     * 부르면 같은 조회가 두 번 나간다).
     */
    let active = true // 마운트 직후, 첫 onActivated 전에도 쿼리 변화에 반응해야 한다
    // ── 단 하나의 watcher. 쿼리가 진실이고 나머지는 전부 파생이다. ──
    watch(
      () => [
        q.value.date, q.value.view, q.value.scope, q.value.userId,
        q.value.deptId, q.value.kinds.join(','), q.value.q
      ].join('|'),
      () => { if (active && onPage()) load() }
    )

    function step(dir) {
      const d = anchorDate.value
      const next = new Date(d)
      // 월 단위 이동은 1일로 맞춘 뒤 옮긴다. 31일에서 setMonth(+1) 을 하면
      // 30일까지인 달을 넘어 그다음 달로 가 버린다(1/31 → 3/3, 2월이 통째로 건너뛰어짐).
      if (q.value.view === 'month' || q.value.view === 'agenda') next.setMonth(d.getMonth() + dir, 1)
      else if (q.value.view === 'week') next.setDate(d.getDate() + dir * 7)
      else next.setDate(d.getDate() + dir)
      patch({ date: ymd(next) })
    }

    function goToday() { patch({ date: ymd(new Date()) }) }
    function setView(v) { patch({ view: v }) }

    function onScopeChange({ scope, userId, deptId }) {
      patch({ scope, userId: userId || '', deptId: deptId || '' })
    }

    function applyKinds() {
      patch({ kinds: kindsDraft.value.join(',') })
      filterOpen.value = false
    }

    function openDay(dateYmd) { openSheet('day', dateYmd) }

    function shiftDay(dir) {
      const next = addDaysYmd(sheetDate.value, dir)
      patch({ sheetId: next, date: next })
    }

    function openItem(item) {
      if (item.kind === 'task') {
        router.push(`/tasks/${item.id}`)
        return
      }
      // 정규화된 AgendaItem 을 그대로 넘긴다. 예전에는 서버 원본(item.raw)을
      // 넘겼는데 EventInfoDialog 가 기대하는 필드명이 달라 담당자·설명·장소가
      // 전부 비고 '관리' 버튼도 뜨지 않았다.
      viewingEvent.value = item
      eventInfoOpen.value = true
    }

    function pickCompose(kind) {
      composeSheetOpen.value = false
      openCompose(kind)
    }

    function openCompose(kind, date) {
      composeDate.value = date || q.value.date
      // 날짜만 정해진 경로(FAB · 일자 시트 · 상단바)는 종일이 기본이다.
      // 드래그로 들어온 시각 범위가 남아 있으면 지운다.
      composeStartAt.value = null
      composeEndAt.value = null
      composeAllDay.value = true
      if (kind === 'task') {
        editingTask.value = null
        taskDialogOpen.value = true
      } else {
        editingEvent.value = null
        eventDialogOpen.value = true
      }
    }

    function onComposeFromSheet({ kind, date }) {
      openCompose(kind, date)
    }

    /**
     * 주/일 타임그리드에서 빈 슬롯을 드래그(또는 종일 칸 클릭)해 새 일정을 만든다.
     * payload: { start: Date, end: Date, allDay: boolean }
     *
     * 이 핸들러가 없어서 그동안 그 드래그가 아무 일도 하지 않았다.
     * 선택 영역은 화면에 그려지는데 손을 떼면 사라지기만 했다.
     */
    function onGridCreate({ start, end, allDay }) {
      if (!start) return
      composeDate.value = ymd(start)
      composeStartAt.value = start
      composeEndAt.value = end || null
      composeAllDay.value = !!allDay
      editingEvent.value = null
      eventDialogOpen.value = true
    }

    /**
     * 타임그리드에서 일정 블록을 끌어 옮겼다.
     * payload: { id: uid, start: Date, end: Date } — id 는 'schedule:12' 형태다.
     *
     * 월 그리드의 onItemMove 와 같은 규칙을 따른다: 낙관적 반영 → 저장 → 실패 시
     * uid 로 되돌리기. 다른 점은 여기서는 **절대 시각**이 온다는 것뿐이다.
     */
    async function onGridUpdate({ id, start, end }) {
      const item = items.value.find((it) => it.uid === id)
      if (!item || !item.canEdit || !start) return

      if (item.kind !== 'schedule') {
        // 업무는 시각이 없다(종일 밴드에 들어간다). 날짜만 평행이동한다.
        const days = diffDaysYmd(ymd(start), item.date)
        if (days) await onItemMove({ item, days })
        return
      }

      /*
       * 반복 일정은 먼저 묻는다 — 월 그리드와 같은 이유다.
       * 서버는 범위 수정에서 날짜를 빼므로 드래그로 가능한 범위는 '이 일정만'
       * 하나뿐이고, 그건 이 건을 시리즈에서 분리한다.
       */
      if (item.seriesId) {
        try {
          await ElMessageBox.confirm(
            '반복되는 일정입니다. 시간을 옮기면 이 건만 바뀌고 반복에서 분리됩니다.',
            '반복 일정 이동',
            { confirmButtonText: '이 일정만 이동', cancelButtonText: '취소', type: 'warning' }
          )
        } catch {
          return
        }
      }

      const startAt = toServerDateTime(start)
      const endAt = toServerDateTime(end || start)
      if (!startAt) return

      const moved = { ...item, startAt, endAt, date: ymd(start), endDate: ymd(end || start) }
      pendingMoves.set(item.uid, moved)
      items.value = items.value.map((it) => (it.uid === item.uid ? moved : it))

      try {
        await store.dispatch('updateEvent', {
          id: item.id,
          eventData: { startDate: startAt, endDate: endAt },
          scope: 'this'
        })
        if (item.seriesId) moved.seriesId = null
        ElMessage.success(`'${item.title}' 시간을 옮겼습니다.`)
        await reload()
      } catch (e) {
        items.value = items.value.map((it) => (it.uid === item.uid ? item : it))
        ElMessage.error(e.response?.data?.message || '시간을 옮기지 못했습니다.')
      } finally {
        pendingMoves.delete(item.uid)
      }
    }

    /** '8월 25일' / '8월 25일 ~ 8월 29일' */
    function movedRangeLabel(item, days) {
      const f = (v) => {
        const d = new Date(`${v}T00:00:00`)
        return `${d.getMonth() + 1}월 ${d.getDate()}일`
      }
      const from = addDaysYmd(item.date, days)
      const to = addDaysYmd(item.endDate || item.date, days)
      return from === to ? f(from) : `${f(from)} ~ ${f(to)}`
    }

    /**
     * 월 그리드에서 칩을 끌어 놓았다. 항목 전체를 days 만큼 **평행이동**한다.
     * 길이도 시각도 그대로다 — 끌기는 옮기기이지 늘리기가 아니다.
     */
    async function onItemMove({ item, days }) {
      if (!days || !item || !item.canEdit) return

      /*
       * 반복 일정은 먼저 묻는다.
       * 서버는 날짜를 시리즈 전체에 적용하지 않는다 — occurrence 마다 날짜가
       * 달라야 하므로 범위 수정(?scope=)에서 startDate/endDate 를 아예 뺀다.
       * 그래서 드래그로 가능한 범위는 '이 일정만' 하나뿐인데, 그건 이 건을
       * 시리즈에서 분리한다(이후 반복 수정에서 빠진다). 조용히 할 일이 아니다.
       * EventScopeDialog 를 쓰지 않는 이유도 같다 — 세 선택지 중 둘은
       * 이 조작을 애초에 수행할 수 없어서, 보여주면 거짓말이 된다.
       */
      if (item.kind === 'schedule' && item.seriesId) {
        try {
          await ElMessageBox.confirm(
            '반복되는 일정입니다. 날짜를 옮기면 이 건만 바뀌고 반복에서 분리됩니다.',
            '반복 일정 이동',
            { confirmButtonText: '이 일정만 이동', cancelButtonText: '취소', type: 'warning' }
          )
        } catch {
          return // 취소. 그리드는 이미 원래 자리로 돌아가 있다.
        }
      }

      /*
       * 낙관적 이동.
       * **여기까지 await 이 없어야** 칩이 원래 날짜로 한 프레임 튀었다가
       * 다시 옮겨지지 않는다 — UnifiedMonthView 는 놓는 순간 미리보기를
       * 걷어내고 이 갱신을 같은 틱에서 받는 것을 전제로 한다.
       * (위 반복 일정 확인 경로는 예외다. 확정 전이니 원위치가 맞다.)
       */
      const moved = shiftAgendaItemDays(item, days)
      pendingMoves.set(item.uid, moved)
      items.value = items.value.map((it) => (it.uid === item.uid ? moved : it))

      try {
        if (item.kind === 'schedule') {
          // 날짜만 보낸다. 서버는 안 보낸 필드를 건드리지 않으므로
          // 제목·설명·장소·유형·색이 그대로 남는다.
          await store.dispatch('updateEvent', {
            id: item.id,
            eventData: {
              startDate: shiftServerDateTime(item.startAt, days),
              endDate: shiftServerDateTime(item.endAt, days)
            },
            scope: 'this'
          })
        } else {
          // 시작일과 마감일 중 **있는 것만** 같은 폭으로 민다.
          // 마감일만 있는 업무에 시작일을 새로 만들어 주면 안 된다.
          await store.dispatch('updateTask', {
            id: item.id,
            data: {
              startDate: item.startDate ? addDaysYmd(item.startDate, days) : undefined,
              dueDate: item.dueDate ? addDaysYmd(item.dueDate, days) : undefined
            }
          })
        }
        // 시리즈에서 분리됐다(서버가 seriesId 를 NULL 로 만든다).
        // 로컬에도 반영해야 이 칩을 다시 끌 때 확인을 또 묻지 않는다.
        if (item.kind === 'schedule' && item.seriesId) moved.seriesId = null

        ElMessage.success(`'${item.title}' → ${movedRangeLabel(item, days)}`)
        await reload()
      } catch (e) {
        // **uid 로** 되돌린다. 배열 참조를 통째로 되돌리면 그 사이 도착한
        // 다른 이동/재조회 결과까지 함께 지워진다.
        items.value = items.value.map((it) => (it.uid === item.uid ? item : it))
        ElMessage.error(e.response?.data?.message || '날짜를 옮기지 못했습니다.')
      } finally {
        pendingMoves.delete(item.uid)
      }
    }

    async function toggleDone(item) {
      if (!item.canProgress) return
      const next = item.status === 'done' ? 'in_progress' : 'done'
      // 체크부터 바꾼다 — 서버는 알림까지 마치고 답해서 기다리면 한 박자 늦게 체크된다.
      // 되돌릴 때는 이동과 같이 uid 로 그 칩만 되돌린다.
      const setStatus = (status) => {
        items.value = items.value.map((it) => (it.uid === item.uid ? { ...it, status } : it))
      }
      setStatus(next)
      try {
        await store.dispatch('setTaskStatus', { id: item.id, status: next })
        await reload()
      } catch (e) {
        setStatus(item.status)
        if (e.handled) {
          // 승인이 필요한 업무다. 캘린더에는 요청 시트가 없으므로 상세로 보낸다.
          ElMessage.warning(
            e.approver?.nickname
              ? `${e.approver.nickname}님의 승인이 필요한 업무입니다.`
              : '결재자 승인이 필요한 업무입니다.'
          )
          router.push(`/tasks/${item.id}`)
          return
        }
        ElMessage.error(e.response?.data?.message || '상태 변경에 실패했습니다.')
      }
    }

    useSwipeNavigation(bodyRef, { onPrev: () => step(-1), onNext: () => step(1) })

    /*
     * 컨트롤 바의 **실측 높이**를 --cal-bar-h 로 내려보낸다.
     *
     * 목록 뷰의 날짜 헤더는 상단바 + 이 바 아래에 달라붙어야 하는데, 예전에는
     * 그 오프셋이 AgendaListView 안의 상수(데스크톱 54px / 모바일 88px)였다.
     * 손으로 셀 수 없는 값이라 실제와 어긋났고(데스크톱 실측 61px — .cal__navbtn
     * 이 --tap-min 44px 이라 min-height:54px 를 넘긴다), 무엇보다 이 바는
     * flex-wrap 이라 부서명이 길면 .cal__right 가 3번째 줄로 내려가 높이가
     * 통째로 바뀐다. 그러면 날짜 헤더가 바 뒤에 숨는다.
     * 재는 쪽이 값을 내려주면 두 곳이 어긋날 수 없다.
     */
    let barRO = null
    onMounted(() => {
      if (!barRef.value || typeof ResizeObserver === 'undefined') return
      const el = barRef.value
      barRO = new ResizeObserver(([entry]) => {
        const h = entry.borderBoxSize?.[0]?.blockSize ?? entry.target.getBoundingClientRect().height
        el.closest('.cal')?.style.setProperty('--cal-bar-h', `${Math.round(h)}px`)
      })
      barRO.observe(el)
    })
    onBeforeUnmount(() => { barRO?.disconnect(); barRO = null })

    watch(filterOpen, (v) => { if (v) kindsDraft.value = [...q.value.kinds] })

    /*
     * 상단바의 '새로 만들기' 는 화면을 옮기고 `?compose=` 를 남긴다.
     * 다이얼로그를 상단바가 직접 들면 저장 후 어느 목록을 다시 그려야 할지
     * 알 수 없다. 여는 것은 그 화면의 책임이다.
     * 연 뒤에는 쿼리를 지운다 — 남겨 두면 뒤로가기로 돌아왔을 때 또 열린다.
     */
    watch(
      () => route.query.compose,
      (v) => {
        if (!onPage() || (v !== 'schedule' && v !== 'task')) return
        openCompose(v)
        patch({ compose: '' })
      },
      { immediate: true }
    )

    // 데스크톱에서 공유한 ?view=week 링크를 모바일에서 열면 일 뷰로 맞춘다
    watch(isMobile, (m) => { if (m && onPage() && q.value.view === 'week') patch({ view: 'day' }) }, { immediate: true })

    onMounted(async () => {
      if (!orgUsers.value.length) store.dispatch('fetchOrgUsers').catch(() => {})
      if (!departments.value.length) store.dispatch('fetchDepartments').catch(() => {})
      await load()
    })

    /*
     * 다른 화면에서 돌아왔을 때. 떠나 있는 동안 쿼리 watcher 는 잠자고 있었으므로
     * 지금 URL 기준으로 다시 맞춘다. 캐시는 비우고 받는다 — 업무 상세에서 상태를 바꾸거나
     * 업무를 만들고 돌아와도 업무 쪽 저장은 아젠다 캐시를 모르므로, 캐시를 타면
     * 최대 60초 동안 옛 상태가 그대로 보였다. 첫 활성화는 onMounted 가 이미 불렀다.
     */
    onDeactivated(() => { active = false })
    onActivated(() => {
      if (active) return
      active = true
      store.dispatch('invalidateAgenda')
      load()
    })

    return {
      q, bodyRef, barRef, items, backlog, loading, anchorDate, composeDateObj, periodLabel,
      sheetDate, isTimeGrid,
      orgUsers, departments, availableViews, kindsFiltered,
      filterOpen, composeSheetOpen, pickCompose, kindsDraft, daySheetOpen, dayItems,
      eventDialogOpen, taskDialogOpen, eventInfoOpen,
      editingEvent, editingTask, viewingEvent, composeDate,
      composeEndObj, composeAllDay,
      step, goToday, setView, onScopeChange, applyKinds,
      openDay, shiftDay, openItem, openCompose, onComposeFromSheet,
      onGridCreate, onGridUpdate,
      onItemMove, toggleDone, reload
    }
  }
}
</script>

<style scoped>
/*
 * 셸이 상/하단 크롬만큼 padding 을 주므로 여기서 남은 높이를 전부 차지하게 한다.
 * min-height:100% 는 부모에 확정 높이가 없어 동작하지 않았고, 그래서 백로그를
 * 펼쳤을 때 화면 아래쪽에 빈 공간이 남았다.
 */
.cal {
  display: flex;
  flex-direction: column;
  min-height: calc(100dvh - var(--topbar-h) - var(--safe-top) - var(--nav-bottom-h) - var(--safe-bottom));
}

/* dvh 미지원 브라우저 폴백 */
@supports not (height: 100dvh) {
  .cal { min-height: calc(100vh - var(--topbar-h) - var(--nav-bottom-h)); }
}

/*
 * 주/일 뷰: 높이를 화면에 고정하고 타임그리드가 그 안에서 스크롤한다(isTimeGrid 주석).
 * 본문이 남은 높이를 다 갖고, 그리드는 그 높이를 채운다.
 * vh 줄은 dvh 미지원 브라우저용 폴백이다(지원하면 아래 줄이 이긴다).
 */
.cal--fill {
  height: calc(100vh - var(--topbar-h) - var(--safe-top) - var(--nav-bottom-h) - var(--safe-bottom));
  height: calc(100dvh - var(--topbar-h) - var(--safe-top) - var(--nav-bottom-h) - var(--safe-bottom));
}
/* 태블릿 이상은 하단 탭바가 없다(shell.css). --nav-bottom-h 를 빼면 아래에 60px 빈 띠가 남는다. */
@media (min-width: 768px) { /* --bp-mobile-max + 1 */
  .cal--fill {
    height: calc(100vh - var(--topbar-h) - var(--safe-top) - var(--safe-bottom));
    height: calc(100dvh - var(--topbar-h) - var(--safe-top) - var(--safe-bottom));
  }
}
.cal--fill .cal__body { display: flex; flex-direction: column; overflow: hidden; }
.cal--fill .cal__body > * { flex: 1 1 auto; min-height: 0; }

.cal__bar {
  display: flex;
  flex-shrink: 0; /* 주/일 뷰(.cal--fill)에서 높이가 고정되면 줄어든 만큼 버튼 줄이 그리드 밑으로 숨는다 */
  align-items: center;
  gap: var(--sp-2);
  padding: var(--sp-2) var(--sp-5);
  min-height: 54px;
  background: var(--color-surface);
  border-bottom: 1px solid var(--color-border);
  position: sticky;
  top: calc(var(--topbar-h) + var(--safe-top));
  z-index: var(--z-subbar);
  flex-wrap: wrap;
}

.cal__nav { display: flex; align-items: center; gap: 2px; flex-shrink: 0; }

.cal__navbtn {
  /* 월 이동은 캘린더에서 가장 자주 눌리는 버튼이다. 44×44 아래로 내리지 말 것 */
  width: var(--tap-min);
  height: var(--tap-min);
  display: flex;
  align-items: center;
  justify-content: center;
  border: 0;
  background: none;
  border-radius: var(--r-md);
  color: var(--color-text-muted);
  cursor: pointer;
}
.cal__navbtn:hover { background: var(--color-surface-2); color: var(--color-text); }
.cal__navbtn:active { background: var(--color-border); }

.cal__today {
  height: 34px;
  padding: 0 var(--sp-3);
  border: 1px solid var(--color-border-strong);
  border-radius: var(--r-md);
  background: var(--color-surface);
  font-family: inherit;
  font-size: var(--fs-md);
  font-weight: var(--fw-medium);
  color: var(--color-text);
  cursor: pointer;
  flex-shrink: 0;
}
.cal__today:hover { background: var(--color-surface-2); }

/*
 * 기간 제목은 상단바의 '캘린더' 와 역할이 다르다 — 저건 화면 이름이고
 * 이건 지금 보고 있는 범위다. 화면 이름보다 크게 둔다.
 */
.cal__period {
  margin: 0;
  font-size: var(--fs-title);
  font-weight: var(--fw-semibold);
  letter-spacing: -0.02em;
  color: var(--color-text);
  white-space: nowrap;
}

.cal__right {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  min-width: 0;
}

.cal__filter {
  width: 34px;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--color-border-strong);
  border-radius: var(--r-md);
  background: var(--color-surface);
  color: var(--color-text-muted);
  cursor: pointer;
  position: relative;
  flex-shrink: 0;
}
.cal__filter:hover { background: var(--color-surface-2); color: var(--color-text); }
.cal__filter.is-on { color: var(--color-primary); border-color: var(--color-primary); }
.cal__filter.is-on::after {
  content: '';
  position: absolute;
  top: 4px;
  right: 4px;
  width: 6px;
  height: 6px;
  border-radius: var(--r-full);
  background: var(--color-primary);
}

.cal__body { flex: 1 1 auto; min-height: 0; }

.cal__kinds { display: flex; flex-direction: column; gap: var(--sp-2); }

/* FAB 의 '무엇을 추가할까요' 시트.
   min-height 56px 는 이 앱의 터치 타겟 기준이다(MoveStatusSheet .ms-item 과 동일). */
.cal__pick { display: flex; flex-direction: column; gap: var(--sp-2); }
.cal__pick-btn {
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 100%;
  min-height: 56px;
  padding: var(--sp-3) var(--sp-4);
  border: 1px solid var(--color-border);
  border-radius: var(--r-md);
  background: var(--color-surface);
  font-family: inherit;
  color: var(--color-text);
  text-align: left;
  cursor: pointer;
}
.cal__pick-btn:active { background: var(--color-surface-2); }
.cal__pick-t { font-size: var(--fs-md); font-weight: var(--fw-semibold); }
.cal__pick-d { font-size: var(--fs-sm); color: var(--color-text-muted); }

@media (max-width: 767px) { /* --bp-mobile-max */
  .cal__bar { padding: var(--sp-2) var(--sp-3); gap: var(--sp-1); }
  /* 기간을 첫 줄에 단독으로 올린다. 좁은 폭에서 '2026년 8월' 이
     버튼들 사이에 끼면 가장 중요한 정보가 가장 안 읽힌다. */
  .cal__period { font-size: var(--fs-lg); order: -1; width: 100%; padding: 2px 2px 0; }
  /* 모바일에서는 탭 대상이므로 44px 로 되돌린다 */
  .cal__today { height: var(--tap-min); border-radius: var(--r-full); }
  .cal__filter { width: var(--tap-min); height: var(--tap-min); border-radius: var(--r-full); }
  /*
   * 390px 에서 둘째 줄에 [오늘][‹ ›][범위][필터][월 일 목록] 이 다 들어가지 않아
   * 범위 버튼이 '⋮' 한 글자로 잘렸다(누구 일정을 보고 있는지가 사라짐).
   * 뷰 전환을 첫 줄 기간 옆으로 올리고, 둘째 줄은 이동 + 범위 + 필터만 남긴다.
   * .cal__right 를 contents 로 풀어야 그 안의 자식을 각자 다른 줄로 보낼 수 있다.
   * ::after 는 두 줄 사이의 강제 줄바꿈이다(flex-basis 100% 인 빈 항목).
   */
  .cal__right { display: contents; }
  .cal__period { order: -3; flex: 1 1 auto; width: auto; min-width: 0; }
  /* ViewSwitcher 는 모바일에서 width:100% 로 한 줄을 다 먹는다. 기간 옆에 앉히려면 폭을 풀고
     칸마다 좌우 여백을 돌려준다(높이는 ViewSwitcher 의 --tap-min 그대로). */
  .cal__switch { order: -2; width: auto; flex: 0 0 auto; }
  .cal__switch :deep(.vs-item) { padding: 0 var(--sp-3); }
  .cal__bar::after { content: ''; order: -1; flex-basis: 100%; }
  .cal__nav { margin-right: auto; } /* 범위·필터를 둘째 줄 오른쪽 끝으로 */
}
</style>
